import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Skeleton,
  Divider,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import type { TrafficChannel } from '@ads-manager/shared';
import { useChannelStore } from '../../store';
import { useContractorChannels } from '../../hooks';

export function HeaderChannelSelector() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { selectedChannel, availableChannels, setSelectedChannel, initializeChannel } =
    useChannelStore();

  // Use contractor-specific channels endpoint
  const { data: contractorChannels = [], isLoading } = useContractorChannels();

  // Extract TrafficChannel objects from ContractorChannelItem
  const channelsData = useMemo(
    () => contractorChannels.map((cc) => cc.trafficChannel),
    [contractorChannels]
  );

  // Initialize channels from API
  useEffect(() => {
    if (channelsData.length > 0) {
      initializeChannel(channelsData);
    }
  }, [channelsData, initializeChannel]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectChannel = (channel: TrafficChannel) => {
    setSelectedChannel(channel);
    handleClose();
  };

  // Don't render if no channels available
  if (!isLoading && availableChannels.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ mr: 2 }}>
        <Skeleton variant="rounded" width={120} height={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ ml: 2 }}>
      <Button
        id="channel-selector-button"
        aria-controls={open ? 'channel-selector-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant="outlined"
        size="small"
        endIcon={<KeyboardArrowDown />}
        sx={{
          color: 'white',
          borderColor: 'rgba(255, 255, 255, 0.5)',
          textTransform: 'none',
          '&:hover': {
            borderColor: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {selectedChannel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{selectedChannel.name}</Typography>
          </Box>
        )}
      </Button>
      <Menu
        id="channel-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'channel-selector-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 1, display: 'block' }}
        >
          Выберите канал
        </Typography>
        <Divider />
        {availableChannels.map((channel) => {
          const isSelected = channel.id === selectedChannel?.id;
          return (
            <MenuItem
              key={channel.id}
              onClick={() => handleSelectChannel(channel)}
              selected={isSelected}
            >
              <ListItemText
                primary={channel.name}
                sx={channel.color ? { color: isSelected ? channel.color : 'inherit' } : undefined}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export default HeaderChannelSelector;
